import React, { useState } from 'react';
import { FaUsers, FaUserShield, FaChalkboardTeacher, FaLayerGroup, FaBolt, FaHistory, FaCalendarCheck } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { AdminAPI } from '../../utils/adminApi';
import { getToken } from '../../utils/auth';
import { database } from '../../config/firebase';
import { ref, remove, query, orderByChild, endAt, get } from 'firebase/database';
import ConfirmDialog from './ConfirmDialog';

interface AdminDashboardProps {
    stats: any;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ stats }) => {
    const [cleaning, setCleaning] = useState(false);

    // State for confirm dialog
    const [confirmConfig, setConfirmConfig] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        action: () => Promise<void>;
    }>({
        isOpen: false,
        title: '',
        message: '',
        action: async () => { }
    });

    if (!stats) return null;

    const handleCleanupClick = (days: number) => {
        setConfirmConfig({
            isOpen: true,
            title: 'Xác Nhận Dọn Dẹp',
            message: `Bạn có chắc muốn xoá lịch sử chat ${days === 0 ? 'TẤT CẢ' : `cũ hơn ${days} ngày`}? Hành động này không thể hoàn tác.`,
            action: () => performCleanup(days)
        });
    };

    const performCleanup = async (days: number) => {
        setCleaning(true);
        const toastId = toast.loading('Đang dọn dẹp...');

        try {
            const token = getToken();
            if (!token) throw new Error('Vui lòng đăng nhập lại');

            // 1. Clean Local Images (Backend)
            const apiRes = await AdminAPI.cleanupChat(days, token);

            // 2. Clean Firebase Messages
            let firebaseCount = 0;
            const messagesRef = ref(database, 'chat/messages');

            if (days === 0) {
                // Delete everything
                await remove(messagesRef);
                firebaseCount = -1; // Unknown count, technically all
            } else {
                // Delete messages older than X days
                const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
                const q = query(messagesRef, orderByChild('timestamp'), endAt(cutoff));

                const snapshot = await get(q);
                if (snapshot.exists()) {
                    const promises: Promise<void>[] = [];
                    snapshot.forEach((child) => {
                        promises.push(remove(ref(database, `chat/messages/${child.key}`)));
                        firebaseCount++;
                    });
                    await Promise.all(promises);
                }
            }

            toast.success(`Dọn dẹp thành công! ${apiRes.message}. (Firebase: ${firebaseCount === -1 ? 'All' : firebaseCount} msgs)`, { id: toastId });

        } catch (error: any) {
            console.error('Cleanup failed:', error);
            toast.error(error.message || 'Có lỗi xảy ra', { id: toastId });
        } finally {
            setCleaning(false);
        }
    };

    const StatCard = ({ title, value, sub, icon: Icon, colorClass, bgClass }: any) => (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300 group">
            <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                    <p className="text-gray-500 dark:text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">{title}</p>
                    <h3 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-1">{value}</h3>
                    <div className="flex items-center text-sm font-medium text-gray-500 dark:text-gray-400 mt-2">
                        <span className="bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-lg">
                            {sub}
                        </span>
                    </div>
                </div>
                <div className={`p-4 rounded-xl ${bgClass} group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                    <Icon className={`w-7 h-7 ${colorClass}`} />
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Top Stats Grid - 4 columns for better balance */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
                <StatCard
                    title="Tổng Người Dùng"
                    value={stats.overview.totalUsers}
                    sub={`${stats.overview.totalAdmins} admin`}
                    icon={FaUsers}
                    colorClass="text-blue-600 dark:text-blue-400"
                    bgClass="bg-blue-100 dark:bg-blue-900/30"
                />
                <StatCard
                    title="Tổng Lớp Học"
                    value={stats.overview.totalClasses}
                    sub={`${stats.overview.publicClasses} công khai`}
                    icon={FaChalkboardTeacher}
                    colorClass="text-emerald-600 dark:text-emerald-400"
                    bgClass="bg-emerald-100 dark:bg-emerald-900/30"
                />
                <StatCard
                    title="Tổng Quiz"
                    value={stats.overview.totalQuizzes}
                    sub={`${stats.overview.publishedQuizzes} đã xuất bản`}
                    icon={FaLayerGroup}
                    colorClass="text-purple-600 dark:text-purple-400"
                    bgClass="bg-purple-100 dark:bg-purple-900/30"
                />
                <StatCard
                    title="Đăng Ký Gần Đây"
                    value={stats.overview.recentRegistrations || 0}
                    sub="7 ngày qua"
                    icon={FaUserShield}
                    colorClass="text-rose-600 dark:text-rose-400"
                    bgClass="bg-rose-100 dark:bg-rose-900/30"
                />
            </div>

            {/* Lists Grid - Equal width for better visual balance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Recent Users */}
                <div className="bg-white dark:bg-gray-800 p-5 sm:p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 flex flex-col">
                    <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-100 dark:border-gray-700">
                        <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3">
                            <div className="p-2 sm:p-2.5 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl text-indigo-600 dark:text-indigo-400">
                                <FaHistory className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                            <span className="text-sm sm:text-base">Người Dùng Mới</span>
                        </h3>
                        <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
                            {stats.recentUsers.length}
                        </span>
                    </div>
                    <div className="space-y-2 sm:space-y-3 flex-1 overflow-y-auto max-h-80 sm:max-h-96">
                        {stats.recentUsers.length > 0 ? (
                            stats.recentUsers.map((user: any) => (
                                <div key={user.id} className="flex items-center justify-between p-3 sm:p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all border border-transparent hover:border-gray-200 dark:hover:border-gray-600 group">
                                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-xs sm:text-sm font-bold text-white shadow-md group-hover:shadow-lg transition-all flex-shrink-0">
                                            {user.email.substring(0, 2).toUpperCase()}
                                        </div>
                                        <div className="flex flex-col min-w-0 flex-1">
                                            <span className="text-gray-900 dark:text-white font-semibold truncate text-xs sm:text-sm">{user.email}</span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.name || 'Chưa đặt tên'}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 sm:gap-2 text-xs text-gray-500 dark:text-gray-400 ml-2">
                                        <FaCalendarCheck className="w-3 h-3" />
                                        <span className="hidden sm:inline">{new Date(user.created_at).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center h-32 sm:h-40 text-gray-400">
                                <FaHistory className="w-10 h-10 sm:w-12 sm:h-12 mb-3 opacity-30" />
                                <p className="text-sm">Chưa có dữ liệu</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Most Active Users */}
                <div className="bg-white dark:bg-gray-800 p-5 sm:p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 flex flex-col">
                    <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-100 dark:border-gray-700">
                        <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3">
                            <div className="p-2 sm:p-2.5 bg-amber-100 dark:bg-amber-900/30 rounded-xl text-amber-600 dark:text-amber-400">
                                <FaBolt className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                            <span className="text-sm sm:text-base">Hoạt Động Nổi Bật</span>
                        </h3>
                        <span className="text-xs font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
                            Top {stats.mostActiveUsers.length}
                        </span>
                    </div>
                    <div className="space-y-2 sm:space-y-3 flex-1 overflow-y-auto max-h-80 sm:max-h-96">
                        {stats.mostActiveUsers.length > 0 ? (
                            stats.mostActiveUsers.map((user: any, idx: number) => (
                                <div key={user.id} className="flex items-center justify-between p-3 sm:p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all border border-transparent hover:border-gray-200 dark:hover:border-gray-600 group">
                                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                        <div className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-xs sm:text-sm font-bold rounded-xl shadow-sm flex-shrink-0 ${idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' :
                                            idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                                                idx === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                                                    'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                            }`}>
                                            #{idx + 1}
                                        </div>
                                        <span className="text-gray-900 dark:text-white font-semibold truncate text-xs sm:text-sm flex-1">{user.email}</span>
                                    </div>
                                    <div className="flex items-center gap-2 ml-2">
                                        <div className="flex flex-col items-end">
                                            <span className="text-white font-bold bg-gradient-to-r from-blue-500 to-blue-600 px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm shadow-md">
                                                {user.session_count}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 hidden sm:inline">sessions</span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center h-32 sm:h-40 text-gray-400">
                                <FaBolt className="w-10 h-10 sm:w-12 sm:h-12 mb-3 opacity-30" />
                                <p className="text-sm">Chưa có dữ liệu</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* System Maintenance - Improved Grid Layout */}
            <div className="space-y-4">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-red-100 dark:bg-red-900/30 rounded-xl text-red-600 dark:text-red-400">
                        <FaBolt className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Bảo Trì Hệ Thống</h3>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    {/* Chat Cleanup Card */}
                    <div className="bg-white dark:bg-gray-800 p-5 sm:p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400 flex-shrink-0">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <h4 className="font-bold text-gray-900 dark:text-white mb-1 text-sm sm:text-base">Dọn Dẹp Chat</h4>
                                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                    Xoá lịch sử tin nhắn và ảnh cũ để giải phóng dung lượng
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 sm:gap-3">
                            <button
                                disabled={cleaning}
                                onClick={() => handleCleanupClick(7)}
                                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-xs sm:text-sm font-medium transition-colors text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {'>'} 7 ngày
                            </button>
                            <button
                                disabled={cleaning}
                                onClick={() => handleCleanupClick(30)}
                                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-xs sm:text-sm font-medium transition-colors text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {'>'} 30 ngày
                            </button>
                            <button
                                disabled={cleaning}
                                onClick={() => handleCleanupClick(0)}
                                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Xoá Tất Cả
                            </button>
                        </div>
                    </div>

                    {/* Online Status Reset Card */}
                    <div className="bg-white dark:bg-gray-800 p-5 sm:p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg text-orange-600 dark:text-orange-400 flex-shrink-0">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <h4 className="font-bold text-gray-900 dark:text-white mb-1 text-sm sm:text-base">Trạng Thái Online</h4>
                                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                    Reset danh sách online nếu số lượng hiển thị sai do session cũ
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                setConfirmConfig({
                                    isOpen: true,
                                    title: 'Reset Danh Sách Online?',
                                    message: 'Hành động này sẽ xoá toàn bộ danh sách người đang online để khắc phục lỗi hiển thị. Người dùng thực tế sẽ cần tải lại trang để hiển thị lại trạng thái online. Bạn có chắc chắn không?',
                                    action: async () => {
                                        try {
                                            await remove(ref(database, 'chat/presence'));
                                            toast.success("Đã reset danh sách online!");
                                        } catch (e) {
                                            toast.error("Lỗi khi reset");
                                        }
                                    }
                                });
                            }}
                            className="w-full px-3 sm:px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors shadow-sm"
                        >
                            Reset Danh Sách Online
                        </button>
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                title={confirmConfig.title}
                message={confirmConfig.message}
                isDestructive={true}
                onCancel={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
                onConfirm={async () => {
                    setConfirmConfig({ ...confirmConfig, isOpen: false });
                    await confirmConfig.action();
                }}
            />
        </div>
    );
};

export default AdminDashboard;
